import { AnimatePresence, motion } from "framer-motion";
import { Sparkles, Wallet, X } from "lucide-react";

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";

const STRATEGY_COST = 120;

/**
 * Confirmation modal shown between step 5 and the actual generation.
 * Summarizes all 5 inputs and surfaces the cost + balance explicitly.
 */
export default function ConfirmModal({
    open,
    onClose,
    onConfirm,
    form,
    resolvedNiche,
    balance,
}) {
    const canAfford = balance >= STRATEGY_COST;
    const freq = form.frequency || {};

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent
                className="max-w-xl border-[#1F2A40] bg-[#0B0F19] text-[#E8ECF5]"
                data-testid="strategy-confirm-modal"
            >
                <DialogHeader>
                    <DialogTitle className="text-xl font-medium tracking-wide">
                        Ready to generate?
                    </DialogTitle>
                    <DialogDescription className="font-mono text-[11px] italic text-[#8A93A6]">
                        Pulse will orchestrate a {form.duration_days}-day
                        content roadmap tailored to the inputs below.
                    </DialogDescription>
                </DialogHeader>

                <div
                    className="flex flex-col divide-y divide-[#1F2A40] rounded-xl border border-[#1F2A40] bg-[#111726]"
                    data-testid="strategy-confirm-summary"
                >
                    <SummaryRow
                        label="Niche"
                        value={resolvedNiche || "—"}
                        testId="confirm-row-niche"
                    />
                    <SummaryRow
                        label="Duration · Goals"
                        value={`${form.duration_days} days · ${(form.goals || []).join(", ") || "—"}`}
                        testId="confirm-row-duration"
                    />
                    <SummaryRow
                        label="Audience · Tone"
                        value={`${form.audience || "—"} · ${(form.tone || []).join(", ") || "—"}`}
                        testId="confirm-row-audience"
                    />
                    <SummaryRow
                        label="Frequency · Formats"
                        value={`${freq.reels_per_day || 0}R · ${freq.stories_per_day || 0}S · ${freq.posts_per_day || 0}P / day · ${(form.formats || []).join(", ") || "—"}`}
                        testId="confirm-row-frequency"
                    />
                    <SummaryRow
                        label="Offer"
                        value={form.product_offer || "—"}
                        testId="confirm-row-offer"
                        clamp
                    />
                </div>

                <div className="flex flex-col gap-3 rounded-xl border border-[#1F2A40] bg-[#111726] px-4 py-3">
                    <div className="flex items-center justify-between">
                        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8A93A6]">
                            cost
                        </span>
                        <span
                            className="font-mono text-sm tabular-nums text-[#E8ECF5]"
                            data-testid="strategy-confirm-cost"
                        >
                            Generate Custom Growth Strategy for{" "}
                            <span className="sp-glow-mint font-semibold">
                                {STRATEGY_COST} NXT
                            </span>
                        </span>
                    </div>
                    <div className="flex items-center justify-between">
                        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8A93A6]">
                            your balance
                        </span>
                        <span
                            className={[
                                "inline-flex items-center gap-1 font-mono text-sm tabular-nums",
                                canAfford
                                    ? "text-[#E8ECF5]"
                                    : "text-[#FF5470]",
                            ].join(" ")}
                            data-testid="strategy-confirm-balance"
                        >
                            <Wallet className="h-3.5 w-3.5" />
                            {balance} NXT
                        </span>
                    </div>
                    <AnimatePresence>
                        {!canAfford && (
                            <motion.p
                                initial={{ opacity: 0, y: -4 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                className="font-mono text-[11px] text-[#FF5470]"
                                data-testid="strategy-confirm-low-balance"
                            >
                                Not enough NXT — top up or upgrade to continue.
                            </motion.p>
                        )}
                    </AnimatePresence>
                </div>

                <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="strategy-confirm-back"
                        className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-[#1F2A40] bg-[#111726] px-4 py-2 text-xs font-medium uppercase tracking-[0.14em] text-[#E8ECF5] transition-all hover:border-[#00F5D4]/50 hover:text-[#00F5D4]"
                    >
                        <X className="h-3.5 w-3.5" />
                        Back to edit
                    </button>
                    <button
                        type="button"
                        disabled={!canAfford}
                        onClick={() => canAfford && onConfirm?.()}
                        data-testid="strategy-confirm-generate"
                        className={[
                            "inline-flex items-center justify-center gap-1.5 rounded-lg px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-white transition-all",
                            canAfford
                                ? "bg-gradient-to-r from-[#2F58CD] to-[#2F58CD] hover:shadow-[0_0_22px_rgba(0,245,212,0.45)]"
                                : "cursor-not-allowed bg-[#1F2A40] opacity-60",
                        ].join(" ")}
                    >
                        <Sparkles className="h-3.5 w-3.5" />
                        Generate (−{STRATEGY_COST} NXT)
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function SummaryRow({ label, value, testId, clamp }) {
    return (
        <div
            className="flex items-start justify-between gap-4 px-4 py-2.5"
            data-testid={testId}
        >
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8A93A6]">
                {label}
            </span>
            <span
                className={[
                    "max-w-[66%] text-right text-xs text-[#E8ECF5]",
                    clamp && "line-clamp-3",
                ]
                    .filter(Boolean)
                    .join(" ")}
            >
                {value}
            </span>
        </div>
    );
}
